import { useCallback, useRef } from 'react';
import type { PlayerRef } from "@remotion/player";
import type { AppConfig } from "@/lib/types";
import { getResolution, getEffectiveDuration } from "@/lib/utils";
import { DEFAULT_CONFIG } from "@/lib/constants";
import type { useTimeline } from "./timeline/useTimeline";

interface UseEditorLogicProps {
  config: AppConfig;
  setConfig: (config: AppConfig | ((prev: AppConfig) => AppConfig)) => void;
  timeline: ReturnType<typeof useTimeline>;
}

export function useEditorLogic({ config, setConfig, timeline }: UseEditorLogicProps) {
  const script = config.text.trim();
  const wordCount = script ? script.split(/\s+/).length : 0;
  const lineCount = config.text ? config.text.split("\n").length : 0;

  const handleReset = () => {
    if (confirm("Reset all studio settings to default?")) {
      setConfig(DEFAULT_CONFIG);
    }
  };

  const handleAudioUpload = (type: 'voiceover' | 'music', url: string) => {
    if (!url) {
      setConfig((prev: AppConfig) => ({
        ...prev,
        audio: {
          ...prev.audio,
          [type === 'voiceover' ? 'voiceoverUrl' : 'musicUrl']: url,
          [type === 'voiceover' ? 'voiceoverDuration' : 'musicDuration']: 0,
        }
      }));
      return;
    }

    setConfig((prev: AppConfig) => ({
      ...prev,
      audio: {
        ...prev.audio,
        [type === 'voiceover' ? 'voiceoverUrl' : 'musicUrl']: url,
      }
    }));

    const audioEl = new window.Audio(url);
    audioEl.addEventListener('loadedmetadata', () => {
      setConfig((prev: AppConfig) => ({
        ...prev,
        audio: {
          ...prev.audio,
          [type === 'voiceover' ? 'voiceoverDuration' : 'musicDuration']: audioEl.duration,
        }
      }));
    });
  };

  const [w, h] = getResolution(config.aspectRatio);
  const fps = 30;
  const durationInFrames = Math.max(1, Math.round(getEffectiveDuration(config) * fps));

  const listenerCleanupRef = useRef<(() => void) | null>(null);

  const handlePlayerRef = useCallback((player: PlayerRef | null) => {
    if (listenerCleanupRef.current) {
      listenerCleanupRef.current();
      listenerCleanupRef.current = null;
    }

    timeline.playerRef.current = player;

    if (player) {
      const onFrame = (e: any) => timeline.onFrameUpdate(e.detail.frame);
      const onPlay = () => timeline.setIsPlaying(true);
      const onPause = () => timeline.setIsPlaying(false);

      player.addEventListener('frameupdate', onFrame);
      player.addEventListener('play', onPlay);
      player.addEventListener('pause', onPause);

      listenerCleanupRef.current = () => {
        player.removeEventListener('frameupdate', onFrame);
        player.removeEventListener('play', onPlay);
        player.removeEventListener('pause', onPause);
      };
    }
  }, [timeline]);

  return {
    wordCount,
    lineCount,
    w,
    h,
    fps,
    durationInFrames,
    handleReset,
    handleAudioUpload,
    handlePlayerRef,
  };
}
